import _ from 'lodash';

type AnalyticsObject = {
  visitorId: string;
  timeOfFirstVisit: Date | string;
  channel: string;
  data: {
    visitorId: string;
    firstEventTime: Date | string;
    lastEventTime: Date | string;
    dimensions: Array<DimensionObject>;
    channel: string;
    customer: string;
    currentAnalyticsContainer: {
      analyticsEvents: Array<EventObject>;
      ipAddress: string;
      userAgent: string;
      referrer: string;
      firstEventTime: Date | string;
      lastEventTime: Date | string;
      page: string;
      scrollDepth: number;
      scrollHistory: string;
      mouseMoveHistory: string;
      visitor: string;
    };
  };
};
type DimensionObject = {
  name: string;
  value: string;
  visitor_id: string;
};
type EventObject = {
  event: string;
  subEvent: string;
  startTime?: Date | string;
  endTime?: Date | string;
  xPos?: number;
  yPos?: number;
  propertyName: string;
  value: string;
};

type SendObject = {
  eventLabel?: string;
  eventAction?: string;
  eventCategory?: string;
  timingCategory?: string;
  timingVar?: string;
  timingValue?: number;
  page?: string;
  hitType?: string;
  propertyName?: string;
  value?: string;
};

type ReferrerObject = {
  sessionReferrer: string;
  originalReferrer: string;
  timeOfFirstVisit: Date;
};

type SettingsObject = {
  firstTimeOnPage: Date;
  channel: string;
  userAgent: string;
  referrerInfo: ReferrerObject;
};

interface DimensionMetricType {
  [key: string]: string;
}

interface SetObject {
  [key: string]: string;
}

declare var ga;
declare var $;
declare var window;

export class HavenAnalytics {
  static analyticsObject: AnalyticsObject;
  static dimensionObject: DimensionObject;
  static eventObject: EventObject;
  static oldTracker: UniversalAnalytics.Tracker;

  // Mouse movement properties
  static lastMouseTime: Date;
  static lastScrollTime: Date;
  static lastFocusTime: Date;
  static mouseX: number;
  static mouseY: number;
  static lastMouseX: number;
  static lastMouseY: number;
  static clickX: number;
  static clickY: number;
  static lastScrollTop: number;
  static heartBeat;

  // Dimension and Metric properties
  static dimensionsHash: DimensionMetricType = {};
  static customDimensions: DimensionMetricType = {};
  static metricsHash: DimensionMetricType = {};
  static customMetrics: DimensionMetricType = {};

  // VisitorID properties
  static setVisitorId;

  static create(channel: string, dimensions: DimensionMetricType, metrics: DimensionMetricType): void {
    window.HavenAnalytics.analyticsObject = {
      visitorId: 'Not set',
      timeOfFirstVisit: 'Not set',
      channel: 'Not set',
      data: {
        visitorId: 'Not set',
        firstEventTime: 'Not set',
        lastEventTime: 'Not set',
        dimensions: [],
        channel: 'Not set',
        customer: null,
        currentAnalyticsContainer: {
          analyticsEvents: [],
          ipAddress: 'Not set',
          userAgent: 'Not set',
          referrer: 'Not set',
          firstEventTime: 'Not set',
          lastEventTime: 'Not set',
          page: 'Not set',
          scrollDepth: 0,
          scrollHistory: '',
          mouseMoveHistory: '',
          visitor: 'Not set',
        },
      },
    };
    window.HavenAnalytics.dimensionObject = {
      name: 'Not set',
      value: 'Not set',
      visitor_id: 'Not set',
    };
    window.HavenAnalytics.eventObject = {
      event: 'Analytics',
      subEvent: 'Event',
      startTime: 'Not set',
      endTime: 'Not set',
      xPos: 0,
      yPos: 0,
      propertyName: 'Not set',
      value: 'Not set',
    };
    window.HavenAnalytics.oldTracker = null;

    // Fetch the oldTracker, if it exists
    ga(() => {
      window.HavenAnalytics.oldTracker = ga.getByName('oldTracker');
    });

    const settings = {
      firstTimeOnPage: new Date(),
      channel: channel,
      userAgent: navigator.userAgent,
      referrerInfo: window.HavenAnalytics.setupReferralInfo(new Date()),
    };

    window.HavenAnalytics.buildBaseAnalyticsObject(settings);

    window.HavenAnalytics.setupCustomObjects(dimensions, metrics);
  }

  static send(type: string, sendObject: SendObject): void {
    if (window.HavenAnalytics.oldTracker) {
      ga('oldTracker.set', window.HavenAnalytics.getCustomObjects());
    }
    ga('set', window.HavenAnalytics.getCustomObjects());

    // If sendObject is undefined this is a dimensions only send
    if (sendObject) {
      switch (String(type).toLowerCase()) {
        case 'pageview':
          window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.page = sendObject.page;
          if (window.HavenAnalytics.oldTracker) {
            ga('oldTracker.send', { hitType: 'pageview', page: sendObject.page });
          }
          ga('send', { hitType: 'pageview', page: sendObject.page });

          window.HavenAnalytics.addEvent({
            event: 'Analytics',
            subEvent: 'Page View',
            propertyName: 'page',
            value: sendObject.page,
          });
          break;
        case 'event':
          if (
            String(sendObject.eventLabel).toLowerCase().search('email') === -1 &&
            String(sendObject.eventCategory).toLowerCase().search('email') === -1
          ) {
            if (window.HavenAnalytics.oldTracker) {
              ga('oldTracker.send', {
                hitType: 'event',
                eventLabel: sendObject.eventLabel,
                eventAction: sendObject.eventAction,
                eventCategory: sendObject.eventCategory,
              });
            }
            ga('send', {
              hitType: 'event',
              eventLabel: sendObject.eventLabel,
              eventAction: sendObject.eventAction,
              eventCategory: sendObject.eventCategory,
            });
          }

          window.HavenAnalytics.addEvent(sendObject);
          break;
        case 'timing':
          if (window.HavenAnalytics.oldTracker) {
            ga('oldTracker.send', {
              hitType: 'timing',
              timingCategory: sendObject.timingCategory,
              timingVar: sendObject.timingVar,
              timingValue: sendObject.timingValue,
            });
          }
          ga('send', {
            hitType: 'timing',
            timingCategory: sendObject.timingCategory,
            timingVar: sendObject.timingVar,
            timingValue: sendObject.timingValue,
          });
          window.HavenAnalytics.addEvent(sendObject);
          break;
      }
    }

    window.HavenAnalytics.resetObjectPostSend();
  }

  static set(type: string, setObject: SetObject): void {
    switch (String(type).toLowerCase()) {
      case 'dimension':
        if (setObject) {
          _.each(setObject, (objValue: string, objKey: string) => {
            window.HavenAnalytics.setDimension(objKey, objValue);
          });
        }
        break;
      case 'metric':
        if (setObject) {
          _.each(setObject, (objValue: string, objKey: string) => {
            window.HavenAnalytics.setMetric(objKey, objValue);
          });
        }
        break;
      case 'userid':
        window.HavenAnalytics.analyticsObject.data.customer = setObject.userId;

        if (window.HavenAnalytics.oldTracker) {
          ga('oldTracker.set', '&uid', setObject.userId);
        }
        ga('set', '&uid', setObject.userId);

        window.HavenAnalytics.set('dimension', { UserID: setObject.userId });
        break;
      case 'event':
        window.HavenAnalytics.addEvent(setObject);
        break;
    }
  }

  static stop(eraseCookies: boolean): void {
    window.HavenAnalytics.stopMouseListeners();

    if (eraseCookies) {
      window.HavenAnalytics.eraseCookie('sessionReferrer');
      window.HavenAnalytics.eraseCookie('originalReferrer');
      window.HavenAnalytics.eraseCookie('havenFirstVisit');
    }
  }

  private static buildBaseAnalyticsObject(settings: SettingsObject): void {
    // Start attempting to get the visitorID from GA
    window.HavenAnalytics.setVisitorId = setInterval(window.HavenAnalytics.getVisitorId, 250);
    window.HavenAnalytics.analyticsObject.timeOfFirstVisit = settings.referrerInfo.timeOfFirstVisit;
    window.HavenAnalytics.analyticsObject.data.firstEventTime = settings.firstTimeOnPage;
    window.HavenAnalytics.analyticsObject.data.lastEventTime = settings.firstTimeOnPage;
    window.HavenAnalytics.analyticsObject.channel = settings.channel;
    window.HavenAnalytics.analyticsObject.data.channel = settings.channel;
    window.HavenAnalytics.setupMouseListeners(settings.firstTimeOnPage);
    window.HavenAnalytics.intializeAnalyticsContainer(settings, 'Not set');
  }

  private static intializeAnalyticsContainer(settings: SettingsObject, userIP: string): void {
    window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.ipAddress = userIP;
    window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.userAgent = settings.userAgent;
    window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.referrer =
      settings.referrerInfo.originalReferrer;
    window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.firstEventTime = settings.firstTimeOnPage;
    window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.lastEventTime = settings.firstTimeOnPage;
  }

  private static resetObjectPostSend(): void {
    window.HavenAnalytics.analyticsObject.data.dimensions = [];
    window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.analyticsEvents = [];
    window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.firstEventTime = new Date();
    window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.lastEventTime = new Date();
    window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.page = 'Not set';
    window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.scrollDepth = 0;
    window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.scrollHistory = '';
    window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.mouseMoveHistory = '';
  }

  private static setupMouseListeners(firstTimeOnPage: Date): void {
    window.HavenAnalytics.lastMouseTime = firstTimeOnPage;
    window.HavenAnalytics.lastScrollTime = firstTimeOnPage;
    window.HavenAnalytics.lastFocusTime = firstTimeOnPage;

    window.HavenAnalytics.mouseX = 0;
    window.HavenAnalytics.mouseY = 0;
    window.HavenAnalytics.lastMouseX = 0;
    window.HavenAnalytics.lastMouseY = 0;

    $(window).mousemove((event: JQueryMouseEventObject) => {
      window.HavenAnalytics.mouseX = Math.floor(event.pageX);
      window.HavenAnalytics.mouseY = Math.floor(event.pageY);
    });

    $(window).click((event: JQueryMouseEventObject) => {
      window.HavenAnalytics.clickX = Math.floor(event.pageX);
      window.HavenAnalytics.clickY = Math.floor(event.pageY);
    });

    $(window).focusin((event: JQueryEventObject) => {
      if (event.target.tagName && String(event.target.tagName).toLowerCase() === 'input') {
        setTimeout(() => {
          window.HavenAnalytics.lastFocusTime = new Date();
        }, 250);
      }
    });

    // Establish heartbeat for mousemove and scroll position
    window.HavenAnalytics.heartBeat = setInterval(() => {
      window.HavenAnalytics.mouseEventHandler($(window).scrollTop(), $(window).height());
    }, 100);
  }

  private static stopMouseListeners(): void {
    if (window.HavenAnalytics.heartBeat) {
      clearInterval(window.HavenAnalytics.heartBeat);
    }

    window.HavenAnalytics.heartBeat = null;

    $(window).unbind('mousemove');
    $(window).unbind('click');
    $(window).unbind('focusin');
  }

  private static setupReferralInfo(firstTimeOnPage: Date): ReferrerObject {
    const referrerInfo: ReferrerObject = <ReferrerObject>{};

    // Record first referrer found in the session from any page we arrive on
    let sessionReferrer = window.HavenAnalytics.readCookie('sessionReferrer');

    if (!sessionReferrer) {
      sessionReferrer = document.referrer || 'direct';
      window.HavenAnalytics.createCookie('sessionReferrer', sessionReferrer, 1);
    }
    referrerInfo.sessionReferrer = sessionReferrer;

    // Record the first occurrence of any referrer as the original referrer
    let originalReferrer = window.HavenAnalytics.readCookie('originalReferrer');

    if (!originalReferrer) {
      window.HavenAnalytics.createCookie('originalReferrer', sessionReferrer, 30);
      originalReferrer = sessionReferrer;
    }
    referrerInfo.originalReferrer = originalReferrer;

    // Record the time of the first visit
    const firstVisCookieVal = window.HavenAnalytics.readCookie('havenFirstVisit');
    let timeOfFirstVisit = firstVisCookieVal ? new Date(firstVisCookieVal) : new Date();
    const isFirstVisit = !firstVisCookieVal;

    if (isFirstVisit) {
      timeOfFirstVisit = firstTimeOnPage;
      window.HavenAnalytics.createCookie('havenFirstVisit', timeOfFirstVisit.toString(), 365);
    }

    referrerInfo.timeOfFirstVisit = timeOfFirstVisit;

    return referrerInfo;
  }

  private static setupCustomObjects(dimensions: DimensionMetricType, metrics: DimensionMetricType): void {
    _.each(dimensions, (value: string, key: string) => {
      window.HavenAnalytics.dimensionsHash[key] = 'dimension' + value;
      window.HavenAnalytics.customDimensions[key] = 'Not set';
    });

    _.each(metrics, (value: string, key: string) => {
      window.HavenAnalytics.metricsHash[key] = 'metric' + value;
      window.HavenAnalytics.customMetrics[key] = 'Not set';
    });
  }

  private static getGADimensionVal(key: string) {
    if (window.HavenAnalytics.dimensionsHash[key]) {
      return window.HavenAnalytics.dimensionsHash[key];
    }

    return null;
  }

  private static setDimension(key: string, value: string): void {
    if (window.HavenAnalytics.customDimensions[key] !== value) {
      window.HavenAnalytics.customDimensions[key] = value;

      const dimVal = window.HavenAnalytics.getGADimensionVal(key);

      window.HavenAnalytics.addEvent({
        event: 'Analytics.Set',
        subEvent: 'Dimension',
        propertyName: key,
        value: value,
        startTime: new Date(window.HavenAnalytics.lastFocusTime),
        endTime: new Date(),
      });

      window.HavenAnalytics.addDimension(key, value);

      if (String(dimVal).toLowerCase().search('email') === -1 && String(value).toLowerCase().search('email') === -1) {
        if (window.HavenAnalytics.oldTracker) {
          ga('oldTracker.set', dimVal, value);
        }
        ga('set', dimVal, value);
      }
    }
  }

  private static getGAMetricVal(key: string) {
    if (window.HavenAnalytics.metricsHash[key]) {
      return window.HavenAnalytics.metricsHash[key];
    }

    return null;
  }

  private static setMetric(key: string, value: string): void {
    if (window.HavenAnalytics.customMetrics[key]) {
      window.HavenAnalytics.customMetrics[key] = value;

      const metVal = window.HavenAnalytics.getGAMetricVal(key);

      window.HavenAnalytics.addEvent({
        event: 'Analytics.Set',
        subEvent: 'Metric',
        propertyName: key,
        value: value,
        startTime: new Date(window.HavenAnalytics.lastFocusTime),
        endTime: new Date(),
      });

      window.HavenAnalytics.addDimension(key, value);

      if (window.HavenAnalytics.oldTracker) {
        ga('oldTracker.set', metVal, value);
      }
      ga('set', metVal, value);
    }
  }

  private static getCustomObjects(): DimensionMetricType {
    const results = {};

    _.each(window.HavenAnalytics.customDimensions, (value: string, key: string) => {
      if (value && value !== 'Not set') {
        const dimVal = window.HavenAnalytics.getGADimensionVal(key);

        results[dimVal] = value;
      }
    });
    _.each(window.HavenAnalytics.customMetrics, (value: string, key: string) => {
      if (value && value !== 'Not set') {
        const metVal = window.HavenAnalytics.getGAMetricVal(key);

        results[metVal] = value;
      }
    });

    return results;
  }

  private static getVisitorId(): void {
    const gaCookie = window.HavenAnalytics.readCookie('_ga');

    if (!gaCookie) {
      return;
    } else {
      clearInterval(window.HavenAnalytics.setVisitorId);

      const visitorId = gaCookie.split('.').slice(-2).join('.');

      window.HavenAnalytics.analyticsObject.visitorId = visitorId;
      window.HavenAnalytics.analyticsObject.data.visitorId = visitorId;
      window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.visitor = visitorId;

      window.HavenAnalytics.set('dimension', {
        VisitorID: visitorId,
      });
    }
  }

  private static createCookie(name: string, value: string, days: number): void {
    let expires;

    if (days) {
      const date = new Date();

      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    } else {
      expires = '';
    }

    document.cookie = name + '=' + value + expires + '; path=/';
  }

  private static eraseCookie(name: string): void {
    window.HavenAnalytics.createCookie(name, '', -1);
  }

  private static readCookie(name: string): string {
    const nameEQ = name + '=';
    const cookieArray = document.cookie.split(';');

    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i];

      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1, cookie.length);
      }

      if (cookie.indexOf(nameEQ) === 0) {
        return cookie.substring(nameEQ.length, cookie.length);
      }
    }

    return null;
  }

  private static mouseEventHandler(scrollTop: number, height: number): void {
    const depth = height ? Math.round((10 * scrollTop) / height) : 0;
    const time = new Date().getTime();

    if (depth > window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.scrollDepth) {
      window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.scrollDepth = depth;
    }

    if (window.HavenAnalytics.clickX || window.HavenAnalytics.clickY) {
      window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.mouseMoveHistory +=
        (window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.mouseMoveHistory.length ? ',' : '') +
        '[' +
        (0 - window.HavenAnalytics.clickX) +
        ',' +
        (0 - window.HavenAnalytics.clickY) +
        ',' +
        (time - window.HavenAnalytics.lastMouseTime.getTime()) +
        ']';

      window.HavenAnalytics.clickX = 0;
      window.HavenAnalytics.clickY = 0;
      window.HavenAnalytics.lastMouseTime = new Date(time);
    } else if (
      Math.floor(window.HavenAnalytics.lastMouseX / 10) !== Math.floor(window.HavenAnalytics.mouseX / 10) ||
      Math.floor(window.HavenAnalytics.lastMouseY / 10) !== Math.floor(window.HavenAnalytics.mouseY / 10)
    ) {
      window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.mouseMoveHistory +=
        (window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.mouseMoveHistory.length ? ',' : '') +
        '[' +
        window.HavenAnalytics.mouseX +
        ',' +
        window.HavenAnalytics.mouseY +
        ',' +
        (time - window.HavenAnalytics.lastMouseTime.getTime()) +
        ']';

      window.HavenAnalytics.lastMouseTime = new Date(time);
    }

    if (scrollTop !== window.HavenAnalytics.lastScrollTop) {
      window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.scrollHistory +=
        (window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.scrollHistory.length ? ',' : '') +
        '[' +
        scrollTop +
        ',' +
        (time - window.HavenAnalytics.lastScrollTime.getTime()) +
        ']';

      HavenAnalytics.lastScrollTime = new Date(time);
    }

    window.HavenAnalytics.lastMouseX = window.HavenAnalytics.mouseX;
    window.HavenAnalytics.lastMouseY = window.HavenAnalytics.mouseY;
    window.HavenAnalytics.lastScrollTop = scrollTop;
  }

  private static populateNewEvent(eventObj: EventObject | SendObject): EventObject {
    const newEvent = {
      event: 'Analytics',
      subEvent: 'Event',
      startTime: 'Not set',
      endTime: 'Not set',
      xPos: 0,
      yPos: 0,
      propertyName: 'Not set',
      value: 'Not set',
    };

    _.each(window.HavenAnalytics.eventObject, (value: string | number | Date, key: string) => {
      newEvent[key] = value;
    });

    _.each(eventObj, (value: string | number | Date, key: string) => {
      if (newEvent[key]) {
        newEvent[key] = value;
      }
    });

    return newEvent;
  }

  private static addDimension(dimKey: string, dimValue: string): void {
    const existing = _.find(window.HavenAnalytics.analyticsObject.data.dimensions, (dim: DimensionObject) => {
      return dim.name === dimKey;
    });

    if (existing) {
      existing.value = dimValue;
    } else {
      const dimObject = {
        name: dimKey,
        value: dimValue,
        visitor_id: window.HavenAnalytics.analyticsObject.visitorId,
      };

      window.HavenAnalytics.analyticsObject.data.dimensions.push(dimObject);
    }
  }

  private static addEvent(eventObj: EventObject | SendObject): void {
    const containerPage = window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.page;

    if (!containerPage || containerPage === 'Not set') {
      const currentPage = window.location.pathname + window.location.hash;

      window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.page = currentPage;
    }

    const newEvent = window.HavenAnalytics.populateNewEvent(eventObj);

    if (!newEvent.startTime || newEvent.startTime === 'Not set') {
      newEvent.startTime = new Date(window.HavenAnalytics.lastFocusTime);

      if (newEvent.startTime < window.HavenAnalytics.analyticsObject.data.firstEventTime) {
        window.HavenAnalytics.analyticsObject.data.firstEventTime = newEvent.startTime;
      }

      if (newEvent.startTime < window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.firstEventTime) {
        window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.firstEventTime = newEvent.startTime;
      }
    }
    if (!newEvent.endTime || newEvent.endTime === 'Not set') {
      newEvent.endTime = new Date();

      if (newEvent.endTime > window.HavenAnalytics.analyticsObject.data.lastEventTime) {
        window.HavenAnalytics.analyticsObject.data.lastEventTime = newEvent.endTime;
      }

      if (newEvent.endTime > window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.lastEventTime) {
        window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.lastEventTime = newEvent.endTime;
      }
    }

    if (!newEvent.xPos) {
      newEvent.xPos = window.HavenAnalytics.lastMouseX;
    }

    if (!newEvent.yPos) {
      newEvent.yPos = window.HavenAnalytics.lastMouseY;
    }

    window.HavenAnalytics.analyticsObject.data.currentAnalyticsContainer.analyticsEvents.push(newEvent);
  }
}
